import $ from 'jquery';
import Flickity from 'flickity';
import 'flickity-imagesloaded';
import rimg from 'rimg-shopify';
import layout from '../Layout';
import ProductZoom from './ProductZoom';

export default class ProductImages {
  constructor(el, options) {
    this.el = el;
    this.imagesLayout = options.images_layout;
    this.thumbnailsDirection = options.thumbnails_direction;
    this.navigateToImages = options.navigateToImages;

    this.imagesJson = JSON.parse(el.querySelector('[data-images]').innerHTML);

    this.imagesContainer = el.querySelector('[data-product-images]');
    this.images = el.querySelectorAll('[data-product-image]');
    this.imagesCount = this.images.length;

    this.zoomableImages = el.querySelectorAll('[data-product-image-zoom]');

    this.zoomedImages = [];
    for (let i = 0; i < this.zoomableImages.length; i++) {
      this.zoomedImages.push(new ProductZoom(this.zoomableImages[i]));
    }

    this._setThumbnailsHeight = this._setThumbnailsHeight.bind(this);
    this._updateArrows = this._updateArrows.bind(this);
    this.scrollUpButton = this.el.querySelector('[data-product-images-scroll-up]');
    this.scrollDownButton = this.el.querySelector('[data-product-images-scroll-down]');

    this.onThumbnailClick = this._onThumbnailClick.bind(this);
    this.onScrollClickUp = () => this._scrollImages('up');
    this.onScrollClickDown = () => this._scrollImages('down');

    // Handle layout changes
    this.onLayoutChange = this._adjustLayout.bind(this);
    layout.onBreakpointChange(this.onLayoutChange);

    // Initial positioning
    this._adjustLayout();

    if (this.imagesLayout === 'alternate') {
      this._initThumbnails();
      window.addEventListener('resize', this._setThumbnailsHeight);
    }
  }

  setOptions(options) {
    this.navigateToImages = options.navigateToImages;
  }

  // Show correct image for the selected variant
  selectImageByVariant(variant) {
    if (!variant) return;

    if (!variant.featured_image) {
      this.selectImage();
    } else {
      // Grab selected image element
      const image = this.el.querySelector(`[data-product-image-id="${variant.featured_image.id}"]`);

      this.selectImage(image);

      if (this.imagesLayout === 'standard' && !this.flickity) {
        this._highlightImage(image);
      }
    }
  }

  _setThumbnailsHeight() {
    if (this.images.length === 0) {
      return;
    }

    if (this.imagesLayout !== 'alternate') {
      return;
    }

    const direction = this.thumbnailsDirection;
    const size = direction === 'vertical' ? 'height' : 'width';
    const sizeScroll = direction === 'vertical' ? 'scrollHeight' : 'scrollWidth';

    const available = this.el.querySelector('[data-product-selected-image]').getBoundingClientRect()[size];
    const required = this.imagesContainer[sizeScroll];

    const minimum = 280;

    if (required < available) {
      this.scrollUpButton.style.display = 'none';
      this.scrollDownButton.style.display = 'none';
    } else {
      this.scrollUpButton.style.display = 'block';
      this.scrollDownButton.style.display = 'block';
    }

    if (layout.isBreakpoint('S', 'M')) {
      this.scrollUpButton.style.display = 'none';
      this.scrollDownButton.style.display = 'none';
      this.imagesContainer.parentNode.style.height = '';
      return;
    }

    if (direction === 'vertical') {
      this.imagesContainer.parentNode.style.height = `${available < minimum ? minimum : available}px`;
      this.imagesContainer.style.maxHeight = '5000px';
    }
  }

  selectImage(image = null) {
    const selectableImage = this.el.querySelector('[data-product-selected-image]');

    // Change slideshow slide if we are in mobile
    if (image && this.flickity) {
      const index = image.getAttribute('data-product-image-index');
      this.flickity.select(index, false, false);
    }

    if (!selectableImage) {
      return;
    }

    const defaultId = selectableImage.getAttribute('data-product-selected-image-default');

    let imageHtml = this.imagesJson[defaultId];

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].classList.remove('product-image-selected');
    }

    if (image) {
      const id = image.getAttribute('data-product-image-id');

      imageHtml = this.imagesJson[id];

      image.classList.add('product-image-selected');
    }

    const setThumbnails = () => {
      this._setThumbnailsHeight();
      selectableImage.removeEventListener('rimg:load', setThumbnails);
    };

    selectableImage.innerHTML = imageHtml;
    selectableImage.addEventListener('rimg:load', setThumbnails);
    rimg.watch(selectableImage);
  }

  unload() {
    if (this.flickity) {
      this._destroyFlickity();
    }

    layout.offBreakpointChange(this.onLayoutChange);

    this.zoomedImages.forEach(zoomedImage => zoomedImage.unload());

    window.removeEventListener('resize', this._setThumbnailsHeight);
  }

  _adjustLayout() {
    if (layout.isBreakpoint('L')) {
      if (this.flickity) {
        this._destroyFlickity();
      }
    } else if (layout.isBreakpoint('S', 'M')) {
      if (!this.flickity
        && this.images.length >= 2
        && this.imagesLayout === 'standard'
      ) {
        this._initFlickity();
      }
    }
    this._setThumbnailsHeight();
  }

  _initFlickity() {
    // Reintroduce Flickity adaptive height (featured in v2.0.0 but not v1.2.1)
    Flickity.prototype.setGallerySize = function setGallerySize() {
      if (this.options.setGallerySize) {
        const height = this.options.adaptiveHeight && this.selectedCell ?
          this.selectedCell.size.outerHeight : this.maxCellHeight;
        this.viewport.style.height = `${height}px`;
      }
    };

    const selectFn = Flickity.prototype.select;
    Flickity.prototype.select = function select(...args) {
      selectFn.apply(this, args);
      if (this.options.adaptiveHeight) {
        this.setGallerySize();
      }
    };

    this.flickity = new Flickity(this.imagesContainer, {
      accessibility: true,
      adaptiveHeight: true,
      cellAlign: 'left',
      cellSelector: '[data-product-image]',
      friction: 0.8,
      imagesLoaded: true,
      pageDots: false,
      prevNextButtons: false,
      selectedAttraction: 0.2,
      wrapAround: true,
    });
  }

  _destroyFlickity() {
    this.flickity.destroy();
    this.flickity = null;

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].setAttribute('style', '');
    }
  }

  _initThumbnails() {
    if (!this.scrollUpButton || !this.scrollDownButton) {
      return;
    }

    this.scrollUpButton.addEventListener('click', this.onScrollClickUp);
    this.scrollDownButton.addEventListener('click', this.onScrollClickDown);
    this.imagesContainer.addEventListener('scroll', this._updateArrows);
    this.imagesContainer.addEventListener('rimg:load', this._setThumbnailsHeight, { once: true });

    this._setThumbnailsHeight();
    this._updateArrows();

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].addEventListener('click', this.onThumbnailClick);
    }
  }

  _destroyThumbnails() {
    if (!this.scrollUpButton || !this.scrollDownButton) {
      return;
    }

    this.scrollUpButton.removeEventListener('click', this.onScrollClickUp);
    this.scrollDownButton.removeEventListener('click', this.onScrollClickDown);
    this.imagesContainer.removeEventListener('scroll', this._updateArrows);
    this.imagesContainer.removeEventListener('rimg:load', this._setThumbnailsHeight);

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].removeEventListener('click', this.onThumbnailClick);
    }
  }

  _onThumbnailClick(event) {
    const image = event.currentTarget;

    this.selectImage(image);
  }

  _scrollImages(direction) {
    if (this.thumbnailsDirection === 'horizontal') {
      const directions = {
        up: () => this._scrollHorizontalImages('left'),
        down: () => this._scrollHorizontalImages('right'),
      };
      directions[direction]();
      this._updateArrows();
    } else {
      this._scrollVerticalImages(direction);
      this._updateArrows();
    }
  }

  _scrollVerticalImages(direction) {
    const imagesContainerTop = this.imagesContainer.getBoundingClientRect().top;
    let closestDistance = Infinity;
    let closestIndex = -1;

    for (let i = 0; i < this.images.length; i++) {
      const image = this.images[i];
      const { top, bottom } = image.getBoundingClientRect();
      const distance = Math.floor(Math.abs(imagesContainerTop - (direction === 'up' ? bottom : top)));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    const $imagesContainer = $(this.imagesContainer);
    const navigateToImage = direction === 'up'
      ? this.images[Math.max(closestIndex - 1, 0)]
      : this.images[Math.min(closestIndex + 1, this.images.length - 1)];
    const navigateToImageDistance = navigateToImage.getBoundingClientRect().top - imagesContainerTop;

    $imagesContainer.animate({
      scrollTop: this.imagesContainer.scrollTop + navigateToImageDistance,
    }, { queue: false, duration: 500 });
  }

  _scrollHorizontalImages(direction) {
    const imagesContainerLeft = this.imagesContainer.getBoundingClientRect().left;
    let closestDistance = Infinity;
    let closestIndex = -1;

    for (let i = 0; i < this.images.length; i++) {
      const image = this.images[i];
      const { left, right } = image.getBoundingClientRect();
      const distance = Math.floor(Math.abs(imagesContainerLeft - (direction === 'left' ? right : left)));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    const $imagesContainer = $(this.imagesContainer);
    const navigateToImage = direction === 'left'
      ? this.images[Math.max(closestIndex - 1, 0)]
      : this.images[Math.min(closestIndex + 1, this.images.length - 1)];
    const navigateToImageDistance = navigateToImage.getBoundingClientRect().left - imagesContainerLeft;

    $imagesContainer.animate({
      scrollLeft: this.imagesContainer.scrollLeft + navigateToImageDistance,
    }, { queue: false, duration: 500 });
  }

  _updateArrows() {
    let scrollPosition;
    let totalPixels;
    let visiblePixels;

    if (this.thumbnailsDirection === 'horizontal') {
      const { scrollLeft, scrollWidth, clientWidth } = this.imagesContainer;
      scrollPosition = scrollLeft;
      totalPixels = scrollWidth;
      visiblePixels = clientWidth;
    } else {
      const { scrollTop, scrollHeight, clientHeight } = this.imagesContainer;
      scrollPosition = scrollTop;
      totalPixels = scrollHeight;
      visiblePixels = clientHeight;
    }

    if (scrollPosition === 0) {
      this.scrollUpButton.classList.add('disabled');
      this.scrollDownButton.classList.remove('disabled');
    } else if ((totalPixels - visiblePixels) === scrollPosition) {
      this.scrollUpButton.classList.remove('disabled');
      this.scrollDownButton.classList.add('disabled');
    } else {
      this.scrollUpButton.classList.remove('disabled');
      this.scrollDownButton.classList.remove('disabled');
    }
  }

  _highlightImage(image) {
    if (!image) {
      return;
    }

    // Move image to top if we are not in sticky mode
    if (!this.navigateToImages) {
      this.imagesContainer.insertBefore(image, this.images[0]);
      this.images = this.el.querySelectorAll('[data-product-image]');
    }

    // Fade out non-selected images
    for (let i = 0; i < this.images.length; i++) {
      if (image !== this.images[i]) {
        this.images[i].classList.add('product-image-fade');
      }
    }

    // Scroll to selected image
    let scrollTop = image.getBoundingClientRect().top + window.pageYOffset;
    let scrollContainer = document.querySelectorAll('html, body');

    // If container is smaller than contents we should scroll the container. Otherwise, scroll the body.
    if (this.el.querySelector('[data-product-images-scroll-container]')) {
      const container = this.el.querySelector('[data-product-images-scroll-container]');
      const { clientHeight, scrollHeight } = container;
      if (scrollHeight > clientHeight) {
        scrollContainer = container;
        scrollTop = image.offsetTop;
      }
    }

    $(scrollContainer).animate({ scrollTop }, 'slow', 'swing', () => {
      // Fade back in all the images
      setTimeout(() => {
        for (let i = 0; i < this.images.length; i++) {
          this.images[i].classList.remove('product-image-fade');
        }
      }, 333);
    });
  }
}
